/**
 * VR build-mode state shared by the library panel, the placement tool, the
 * hand manipulator and the hand teleport (which must stand down while you
 * place or hold something).
 */
import { create } from 'zustand';
import type { PrimitiveShape, PrimitiveItem, LightItem, LeaderboardConfig } from '../../../stores/worldStore';

/** A model waiting to be placed with the placement ray. */
export interface ContentPlacement {
  url: string;
  filename: string;
  /** Library (Poly Pizza) models come in arbitrary units: fit to ~1.6 m on load. */
  autoFit?: boolean;
  /** Model's lowest point (metres), so it rests on the surface it's placed on. */
  minY?: number;
  thumbnailUrl?: string | null;
}

/**
 * Something held in the right hand, waiting to be dropped (Horizon-style
 * placement: pick it on the wrist palette, it appears in your hand, release
 * — pinch or trigger — to put it down where it is).
 */
export type HeldItem =
  | { kind: 'primitive'; shape: PrimitiveShape; game?: string; arcadeGame?: string; board?: LeaderboardConfig; template?: PrimitiveItem }
  | { kind: 'content'; spec: ContentPlacement; template?: Record<string, any> }
  | { kind: 'light'; template?: LightItem };

interface VRBuildState {
  /** The item in your hand (see HeldItem). */
  held: HeldItem | null;
  setHeld: (h: HeldItem | null) => void;
  libraryOpen: boolean;
  placing: ContentPlacement | null;
  /** A hand is holding (or two hands are scaling) the selected object. */
  handling: boolean;
  /** Which hand is doing it (null = both, e.g. two-hand resize). The other hand can still teleport. */
  handlingSide: 'left' | 'right' | null;
  setHandlingSide: (s: 'left' | 'right' | null) => void;
  /** The two-hand move / turn / resize owns the selection (VRHandManipulator). A one-hand grab hands over to it. */
  twoHand: boolean;
  setTwoHand: (v: boolean) => void;
  /** What a one-hand grab (VRGrab) is holding — the two-hand gesture takes over that object even if a stray pinch changed the selection. */
  grabbedId: string | null;
  setGrabbedId: (id: string | null) => void;
  /** A resize handle is being dragged (VRGrab) — no two-hand gesture meanwhile. */
  resizing: boolean;
  setResizing: (v: boolean) => void;
  /** Studio job started from VR (survives closing the library). */
  studioJobId: string | null;
  setStudioJobId: (id: string | null) => void;
  setLibraryOpen: (open: boolean) => void;
  setPlacing: (p: ContentPlacement | null) => void;
  setHandling: (h: boolean) => void;
}

export const useVRBuild = create<VRBuildState>((set) => ({
  held: null,
  setHeld: (held) => set({ held }),
  libraryOpen: false,
  placing: null,
  handling: false,
  handlingSide: null,
  setHandlingSide: (handlingSide) => set({ handlingSide }),
  twoHand: false,
  setTwoHand: (twoHand) => set({ twoHand }),
  grabbedId: null,
  setGrabbedId: (grabbedId) => set({ grabbedId }),
  resizing: false,
  setResizing: (resizing) => set({ resizing }),
  studioJobId: null,
  setStudioJobId: (studioJobId) => set({ studioJobId }),
  setLibraryOpen: (libraryOpen) => set({ libraryOpen }),
  setPlacing: (placing) => set({ placing }),
  setHandling: (handling) => set({ handling }),
}));

if (import.meta.env.DEV && typeof window !== 'undefined') (window as any).__gxVRBuild = useVRBuild;
